/**
 * A street race as a job: `raceDef(host, tier)` is a `MissionDef` run by the mission runner (intro card,
 * objective clipping, result sheet, NICKED / WASTED handling all come with it), and `RaceSession` is the
 * race itself — the rivals on the grid, their driving, everyone's checkpoints and the running order.
 *
 * Fair with the law: you can't line up with heat on you; the race itself never calls the police, but
 * driving like a lunatic past a patrol is reckless driving like any other time (src/heat), and the pot is
 * only paid at heat 0.
 */
import type { GameContext } from '../core/context';
import { distance, type V2 } from '../core/math';
import type { Vehicle } from '../vehicles/types';
import type { MissionDef } from '../missions/types';
import { UNLOCK_STREET_JOBS } from '../missions/ids';
import type { ActivityHost } from './host';
import { RACE_GIVER, RACE_ORGANISER, type RaceTier } from './races';
import { gridSlots, type RaceLine } from './roadLine';
import { RivalDriver } from './rivalDriver';
import { advanceTier, formatRaceTime, ordinal, placeOf, racePayout, recordLow, rivalPace, standings, type RacerProgress } from './scoring';
import { RACE_TIERS } from './state';

export const RACE_ID = 'street-race';
const PLAYER = 'you';
/** Seconds of the starter's countdown. */
export const RACE_COUNTDOWN = 3.2;
/** Traffic density while a race runs (night streets), and the radius cleared round the grid. */
export const RACE_TRAFFIC = 0.45;
const GRID_CLEAR = 45;

interface Rival {
  name: string;
  vehicle: Vehicle;
  driver: RivalDriver;
  passed: number;
  finishedAt: number | null;
  out: boolean;
}

export class RaceSession {
  readonly rivals: Rival[] = [];
  /** Race clock (seconds since the flag dropped). */
  clock = 0;
  started = false;
  playerPassed = 0;
  playerFinishedAt: number | null = null;
  /** For each checkpoint, the index of its point on the line (rivals pass it once their driver is beyond). */
  private readonly cpIndex: number[];
  /** Metres along the line at each checkpoint (standings / rubber band). */
  private readonly cpAlong: number[];
  /** Traffic density before the race (night races run on thin traffic; put back afterwards). */
  private density: number | null = null;

  constructor(
    private readonly ctx: GameContext,
    readonly tier: RaceTier,
    readonly line: RaceLine,
  ) {
    const along: number[] = [0];
    for (let i = 1; i < line.points.length; i++) along.push(along[i - 1]! + distance(line.points[i - 1]!, line.points[i]!));
    this.cpIndex = line.checkpoints.map((cp) => {
      let best = 0;
      let bd = Infinity;
      for (let i = 0; i <= line.finishIndex; i++) {
        const d = distance(cp.pos, line.points[i]!);
        if (d < bd) {
          bd = d;
          best = i;
        }
      }
      return best;
    });
    this.cpAlong = this.cpIndex.map((i) => along[i]!);
  }

  get total(): number {
    return this.line.checkpoints.length;
  }

  get playerFinished(): boolean {
    return this.playerFinishedAt !== null;
  }

  /** Put the rivals on the grid beside and behind the player. */
  spawnRivals(): void {
    const vehicles = this.ctx.services.get('vehicles');
    const player = this.ctx.services.get('player');
    // Night racing: the streets are quieter (traffic thins out of sight), and the lads have stopped
    // the traffic either side of the grid so the start isn't a jam.
    const traffic = this.ctx.services.tryGet('traffic');
    if (traffic) {
      this.density = traffic.density;
      traffic.density = Math.min(traffic.density, RACE_TRAFFIC);
      for (const q of vehicles.query(this.line.start, GRID_CLEAR)) {
        if (q === player.vehicle || q.isPolice || !q.tags.has('traffic')) continue;
        traffic.detach(q);
        vehicles.despawn(q);
      }
    }
    const slots = gridSlots(this.line.start, this.line.heading, this.tier.rivals.length);
    this.tier.rivals.forEach((r, i) => {
      const at = slots[i]!;
      for (const q of vehicles.query(at, 4.5)) if (q !== player.vehicle && !q.isPolice && !q.tags.has('mission')) vehicles.despawn(q);
      const v = vehicles.spawn(r.spec, at, {
        heading: this.line.heading,
        controller: 'ai',
        occupant: { kind: 'ped', pedId: null },
        paint: r.paint,
        locked: true,
        tags: ['mission', 'race:rival'],
      });
      this.rivals.push({ name: r.name, vehicle: v, driver: new RivalDriver(this.ctx, v, this.line.points, this.tier.cruise, this.tier.corner), passed: 0, finishedAt: null, out: false });
    });
  }

  begin(): void {
    this.started = true;
    this.clock = 0;
  }

  /** Rivals at the wheel (order band 200–299, before the vehicles step). */
  drive(dt: number): void {
    if (!this.started) return;
    const me = this.metric(this.playerPassed, this.toNext(this.ctx.services.get('player').pos, this.playerPassed));
    for (const r of this.rivals) {
      if (r.out) continue;
      r.driver.pace = rivalPace(1, this.metric(r.passed, this.toNext(r.vehicle.pos, r.passed)) - me);
      r.driver.drive(dt);
    }
  }

  /** Clock, checkpoints and finishes (the race stage's tick). */
  step(dt: number): void {
    if (!this.started) return;
    this.clock += dt;
    const player = this.ctx.services.get('player');
    if (this.playerFinishedAt === null && player.mode === 'driving') {
      const cp = this.line.checkpoints[this.playerPassed];
      if (cp && distance(player.pos, cp.pos) <= cp.radius) {
        this.playerPassed++;
        if (this.playerPassed >= this.total) this.playerFinishedAt = this.clock;
        else this.ctx.services.tryGet('audio')?.ui('tick');
      }
    }
    for (const r of this.rivals) {
      if (r.finishedAt !== null || r.out) continue;
      const v = r.vehicle;
      // Wrecked, or somebody dragged him out of it: out of the race.
      if (v.destroyed || v.controller !== 'ai' || v.occupant === null || v.occupant.kind !== 'ped') {
        r.out = true;
        this.ctx.services.tryGet('ui')?.toast(`${r.name} is out of it.`, 'info', 2.4);
        continue;
      }
      while (r.passed < this.total && r.driver.idx > this.cpIndex[r.passed]!) r.passed++;
      if (r.passed >= this.total) {
        r.passed = this.total;
        r.finishedAt = this.clock;
      }
    }
  }

  progress(): RacerProgress[] {
    const p = this.ctx.services.get('player').pos;
    return [
      { id: PLAYER, passed: this.playerPassed, toNext: this.toNext(p, this.playerPassed), finishedAt: this.playerFinishedAt },
      ...this.rivals.map((r) => ({ id: r.name, passed: r.passed, toNext: this.toNext(r.vehicle.pos, r.passed), finishedAt: r.finishedAt, out: r.out })),
    ];
  }

  place(): number {
    return placeOf(this.progress(), PLAYER);
  }

  /** Who is ahead of the player at the flag (the winner if the player lost). */
  leader(): string {
    return standings(this.progress())[0]?.id ?? PLAYER;
  }

  nextCheckpoint(): V2 {
    const cp = this.line.checkpoints[Math.min(this.playerPassed, this.total - 1)]!;
    return cp.pos;
  }

  /** Checkpoint after the next (shown faintly on the map). */
  afterNext(): V2 | null {
    return this.line.checkpoints[this.playerPassed + 1]?.pos ?? null;
  }

  rivalPositions(): V2[] {
    return this.rivals.filter((r) => !r.out).map((r) => r.vehicle.pos);
  }

  /** Off the grid: rivals still close by stay as abandoned cars (no pop), the rest go. */
  dispose(): void {
    const vehicles = this.ctx.services.get('vehicles');
    const player = this.ctx.services.get('player');
    for (const r of this.rivals) {
      const v = r.vehicle;
      if (!vehicles.get(v.id)) continue;
      v.tags.delete('mission');
      v.tags.delete('race:rival');
      if (player.vehicle === v) continue;
      if (distance(v.pos, player.pos) > 45 || v.destroyed) vehicles.despawn(v);
      else if (v.controller === 'ai') {
        v.controller = 'none';
        v.occupant = null;
      }
    }
    this.rivals.length = 0;
    this.started = false;
    const traffic = this.ctx.services.tryGet('traffic');
    if (traffic && this.density !== null) traffic.density = this.density;
    this.density = null;
  }

  private toNext(p: V2, passed: number): number {
    const cp = this.line.checkpoints[Math.min(passed, this.total - 1)]!;
    return distance(p, cp.pos);
  }

  /** Rough metres along the course: the next gate's distance minus how far off it we are. */
  private metric(passed: number, toNext: number): number {
    if (passed >= this.total) return this.line.length + 1;
    return this.cpAlong[passed]! - toNext;
  }
}

// ----------------------------------------------------------------------------- the job

/** Why the player can't line up yet, or null. */
function gridProblem(host: ActivityHost, tier: RaceTier): string | null {
  const player = host.ctx.services.get('player');
  const v = player.mode === 'driving' ? player.vehicle : null;
  if (!v) return 'Get in a car and line up — on foot you’re only watching.';
  if (v.isPolice) return 'Not in a police car you won’t. Use a different car.';
  if (v.spec.id === 'lorry') return 'Rita won’t start a race with a tipper lorry. Use a car.';
  if (host.ctx.state.heat > 0) return 'The police are after you. Lose them, then come back to the start line.';
  if (!host.canAfford(tier.stake)) return `You need £${tier.stake} to enter. Come back with the money.`;
  return null;
}

export interface RaceRuntime {
  session(): RaceSession | null;
  /** Start a session (spawns the rivals). */
  open(tier: RaceTier, line: RaceLine): RaceSession;
  close(): void;
}

/** The street race for `tier` as a job. `line` comes from `raceLine(layout, tier.route)`. */
export function raceDef(host: ActivityHost, rt: RaceRuntime, tier: RaceTier, line: RaceLine): MissionDef {
  const rec = () => host.record().race;
  const entrants = tier.rivals.length + 1;
  const pot = tier.stake * entrants;
  const limit = Math.round((line.length / 11) * 2.2 + 20);
  const n = tier.tier;
  const session = (): RaceSession | null => rt.session();
  const tagline = `Course ${n} of ${RACE_TIERS}`;
  return {
    id: RACE_ID,
    title: tier.title,
    client: RACE_ORGANISER,
    tag: `STREET RACE · ${tagline.toUpperCase()}`,
    blurb: `Rita Vance’s night street race. Pay £${tier.stake} to enter, beat ${tier.rivals.length} other drivers to the finish and win £${pot}. ${tier.intro}`,
    reward: pot,
    requires: [],
    requiresUnlock: UNLOCK_STREET_JOBS,
    repeatable: true,
    mood: 'night',
    giver: { poi: RACE_GIVER, prompt: `Talk to ${RACE_ORGANISER.split(' ')[0]} — street race`, glyph: '★', whileDriving: true },
    activity: {
      kicker: `Street race · ${tagline.toLowerCase()}`,
      meta: () => {
        const r = rec();
        const best = r.best[n - 1] ?? 0;
        const won = r.cleared.includes(n);
        return `Stake £${tier.stake}, pot £${pot}. ${best > 0 ? `Your best: ${formatRaceTime(best)}.` : 'Not raced yet.'}${won ? ' Won it before.' : ''}`;
      },
      tried: () => rec().runs > 0,
    },
    intro: [
      { who: RACE_ORGANISER, text: `${tier.title}, tonight. Grid’s on ${tier.gridAt}.` },
      { who: RACE_ORGANISER, text: `£${tier.stake} each on my bonnet. ${tier.rivals.map((r) => r.name).join(' and ')} are in. Winner takes £${pot}.` },
    ],
    introFocus: { pos: line.start },
    setup: () => rt.close(),
    stages: [
      {
        id: 'grid',
        kind: 'custom',
        at: { pos: line.start },
        radius: 6,
        text: `Drive to the start line on ${tier.gridAt} and stop. Entry: £${tier.stake}.`,
        hint: 'Any car will do. Stop on the marker.',
        blocked: (m) => distance(m.ctx.services.get('player').pos, line.start) < 14 && gridProblem(host, tier) !== null,
        blockedText: () => gridProblem(host, tier) ?? '',
        done: (m) => {
          const player = m.ctx.services.get('player');
          return player.mode === 'driving' && !!player.vehicle && player.vehicle.speed < 2 && distance(player.pos, line.start) < 7 && gridProblem(host, tier) === null;
        },
        onComplete: (m) => {
          if (!host.spend(tier.stake, 'race-stake')) return m.fail('You don’t have the entry money any more, and Rita doesn’t give credit. Come back with the cash.');
          m.vars.staked = true;
          const s = rt.open(tier, line);
          // Face down the course on the line (the player pulled up roughly; straighten them out).
          const v = m.ctx.services.get('player').vehicle;
          const vehicles = m.ctx.services.get('vehicles');
          for (const q of vehicles.query(line.start, 4)) if (q !== v && !q.isPolice && !q.tags.has('mission')) vehicles.despawn(q);
          s.spawnRivals();
          if (v) {
            v.body.setPose({ x: line.start.x, z: line.start.z }, line.heading);
            v.body.setVelocity({ x: 0, z: 0 }, 0);
          }
        },
      },
      {
        id: 'countdown',
        kind: 'wait',
        seconds: RACE_COUNTDOWN,
        text: 'Get ready — wait for GO…',
        onEnter: (m) => {
          m.ctx.services.get('player').controls.acquire('raceCountdown');
          m.say([
            { who: RACE_ORGANISER, text: 'Three…', seconds: 0.8 },
            { who: RACE_ORGANISER, text: 'Two…', seconds: 0.8 },
            { who: RACE_ORGANISER, text: 'One…', seconds: 0.8 },
            { who: RACE_ORGANISER, text: 'GO!', seconds: 1.2 },
          ]);
        },
        onTick: (m) => {
          const left = Math.ceil(RACE_COUNTDOWN - m.stageTime);
          host.ticket({ head: left > 0 ? String(left) : 'GO', lines: [tier.title.toUpperCase(), `${entrants} on the grid · pot £${pot}`], flash: true });
        },
        onComplete: (m) => {
          m.ctx.services.get('player').controls.release('raceCountdown');
          session()?.begin();
          m.startTimer(limit, 'Too slow: the race timed out, the crowd went home and Rita kept your entry money. Try again.');
        },
      },
      {
        id: 'race',
        kind: 'custom',
        at: { fn: () => session()?.nextCheckpoint() ?? line.start },
        radius: 9,
        text: 'Race! Drive through every checkpoint in order, then cross the finish.',
        hint: 'Take any route you like — but if you miss a checkpoint, go back for it.',
        blocked: (m) => m.ctx.services.get('player').mode !== 'driving',
        blockedText: 'Get back in a car — any car! The race doesn’t wait.',
        done: () => !!session()?.playerFinished,
        progress: () => {
          const s = session();
          return s ? s.playerPassed / s.total : 0;
        },
        onTick: (_m, dt) => {
          const s = session();
          if (!s) return;
          s.step(dt);
          const place = s.place();
          host.ticket({
            head: `${ordinal(place).toUpperCase()} OF ${entrants}`,
            lines: [`Checkpoint ${Math.min(s.playerPassed + 1, s.total)} of ${s.total}`, formatRaceTime(s.clock)],
          });
        },
        onComplete: (m) => {
          const s = session();
          if (!s) return;
          const place = s.place();
          const time = s.playerFinishedAt ?? s.clock;
          m.vars.place = place;
          m.vars.time = time;
          host.ticket(null);
          m.stopTimer();
          if (place > 1) {
            m.fail(`Beaten to the finish — ${s.leader()} won. You were ${ordinal(place)} in ${formatRaceTime(time)}.`);
            return;
          }
          m.say([{ who: RACE_ORGANISER, text: `${formatRaceTime(time)}! Nobody’s touching that tonight.` }]);
        },
      },
      {
        id: 'collect',
        kind: 'escapeHeat',
        text: 'Rita won’t pay out while the police are watching. Lose them.',
        hint: 'Get out of their sight and stay hidden until your wanted level clears.',
      },
    ],
    outro: [{ who: RACE_ORGANISER, text: 'There’s your pot. Same time next week?' }],
    payout: (m) => {
      const r = rec();
      const time = typeof m.vars.time === 'number' ? m.vars.time : 0;
      const best = r.best[n - 1] ?? 0;
      m.vars.record = best > 0 && time > 0 && time < best;
      return racePayout({ stake: tier.stake, entrants, tier: n, firstWin: !r.cleared.includes(n), record: m.vars.record === true });
    },
    resultStamp: 'FIRST PAST THE FLAG',
    failStamp: 'BEATEN',
    resultLine: (m) => {
      const time = typeof m.vars.time === 'number' ? m.vars.time : 0;
      return `${tier.title} in ${formatRaceTime(time)}${m.vars.record === true ? ' — a course record' : ''}. Rita counts the pot into your hand.`;
    },
    onResolved: (m, outcome) => {
      host.ticket(null);
      rt.close();
      if (m.vars.staked !== true) return; // never got to the grid: no run to book
      const r = rec();
      const time = typeof m.vars.time === 'number' ? m.vars.time : 0;
      if (time > 0) recordLow(r, n, time);
      const won = outcome === 'completed';
      const { unlockedNew } = advanceTier(r, n, won, RACE_TIERS);
      host.dirty();
      if (unlockedNew) announceCourse(host, unlockedNew);
    },
  };
}

/** Book note when a new course opens (and when the races first open). */
export function announceCourse(host: ActivityHost, n: number): void {
  const giver = host.ctx.services.get('layout').pois.find((p) => p.id === RACE_GIVER);
  host.post({
    key: `race-course:${n}`,
    from: RACE_ORGANISER,
    kind: 'offer',
    subject: n === 1 ? 'Racing, Thursday nights' : `New course: course ${n} of ${RACE_TIERS}`,
    text:
      n === 1
        ? 'Heard you can drive. Some of the lads race the borough after dark — stake on my bonnet, winner takes the pot.\n\nFind me on the Basin Crane Quay. Bring a motor and the stake.'
        : `You’ve shown them your tail lights. There’s a longer run for those that can handle it — bigger stakes, quicker company.\n\nSame place, the quay.`,
    ...(giver ? { action: { label: 'Mark the quay on the map', mark: { x: giver.pos.x, z: giver.pos.z, label: `${RACE_ORGANISER.split(' ')[0]} — Basin Crane Quay` } } } : {}),
  });
}
